import React from 'react';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  UserIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ClockIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export const Privacy: React.FC = () => {
  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: UserIcon,
      content: [
        {
          subtitle: 'Information You Provide to Us',
          text: 'We collect information you provide directly to us, such as when you create an account, fill out a form, make a purchase, or contact us. This includes your name, email address, phone number, payment information, and any other information you choose to provide.'
        },
        {
          subtitle: 'Information We Collect Automatically',
          text: 'When you use our services, we automatically collect certain information about your device and usage patterns, including your IP address, browser type, operating system, referring URLs, and information about your interactions with our platform.'
        },
        {
          subtitle: 'Workspace and Project Data',
          text: 'When you use SynqTag, we collect and store the content you create, including projects, tasks, comments, files, and other workspace data necessary to provide our services.'
        }
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: DocumentTextIcon,
      content: [
        {
          subtitle: 'Service Provision',
          text: 'We use your information to provide, maintain, and improve our services, process transactions, send administrative information, and respond to your requests and inquiries.'
        },
        {
          subtitle: 'Communication',
          text: 'We may use your information to communicate with you about our services, send you technical notices, updates, security alerts, and support messages.'
        },
        {
          subtitle: 'Analytics and Improvement',
          text: 'We use analytics to understand how our services are used, identify trends, and improve our platform\'s functionality and user experience.'
        }
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: GlobeAltIcon,
      content: [
        {
          subtitle: 'With Your Consent',
          text: 'We may share your information with third parties when we have your explicit consent to do so.'
        },
        {
          subtitle: 'Service Providers',
          text: 'We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, and customer service.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information if required to do so by law or in response to valid requests by public authorities, court orders, or legal processes.'
        },
        {
          subtitle: 'Business Transfers',
          text: 'In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, subject to appropriate confidentiality protections.'
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: LockClosedIcon,
      content: [
        {
          subtitle: 'Security Measures',
          text: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'
        },
        {
          subtitle: 'Encryption',
          text: 'We use industry-standard encryption protocols to protect sensitive data in transit and at rest, including SSL/TLS encryption for data transmission and AES encryption for data storage.'
        },
        {
          subtitle: 'Access Controls',
          text: 'Access to your personal information is restricted to authorized personnel who need access to perform their job functions and are bound by confidentiality obligations.'
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking Technologies',
      icon: GlobeAltIcon,
      content: [
        {
          subtitle: 'Types of Cookies',
          text: 'We use essential cookies for authentication and security, performance cookies to analyze usage patterns, and functional cookies to remember your preferences.'
        },
        {
          subtitle: 'Third-Party Cookies',
          text: 'We may allow third-party service providers to place cookies on your device for analytics, advertising, and other purposes, subject to their own privacy policies.'
        },
        {
          subtitle: 'Cookie Management',
          text: 'You can control cookie settings through your browser preferences, though disabling certain cookies may affect the functionality of our services.'
        }
      ]
    },
    {
      id: 'user-rights',
      title: 'Your Rights and Choices',
      icon: ShieldCheckIcon,
      content: [
        {
          subtitle: 'Access and Portability',
          text: 'You have the right to access your personal information and request a copy of your data in a portable format.'
        },
        {
          subtitle: 'Correction and Deletion',
          text: 'You can update, correct, or delete your personal information through your account settings or by contacting us directly.'
        },
        {
          subtitle: 'Data Processing Rights',
          text: 'Under GDPR and similar laws, you have rights to object to processing, request processing restrictions, and withdraw consent where applicable.'
        },
        {
          subtitle: 'Marketing Communications',
          text: 'You can opt out of marketing communications at any time by following unsubscribe links in emails or updating your communication preferences.'
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: ClockIcon,
      content: [
        {
          subtitle: 'Retention Periods',
          text: 'We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, comply with legal obligations, and resolve disputes.'
        },
        {
          subtitle: 'Account Data',
          text: 'If you delete your account, we will delete your personal information within 30 days, except where retention is required by law or legitimate business purposes.'
        },
        {
          subtitle: 'Backup Data',
          text: 'Information may persist in backup systems for up to 90 days after deletion from active systems for data recovery and security purposes.'
        }
      ]
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: GlobeAltIcon,
      content: [
        {
          subtitle: 'Cross-Border Processing',
          text: 'Your information may be processed and stored in countries other than your country of residence, including the United States, subject to appropriate safeguards.'
        },
        {
          subtitle: 'Transfer Mechanisms',
          text: 'We ensure adequate protection for international transfers through standard contractual clauses, adequacy decisions, or other approved transfer mechanisms.'
        }
      ]
    },
    {
      id: 'children-privacy',
      title: 'Children\'s Privacy',
      icon: ExclamationTriangleIcon,
      content: [
        {
          subtitle: 'Age Restrictions',
          text: 'Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.'
        },
        {
          subtitle: 'Parental Rights',
          text: 'If we learn that we have collected personal information from a child under 13, we will delete that information as quickly as possible. Parents may contact us to review, delete, or refuse further collection of their child\'s information.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-width section-padding text-center">
          <div>
            <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <ShieldCheckIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold mb-6">
              <span className="gradient-text">Privacy Policy</span>
            </h1>
            <p className="text-xl text-ink-soft mb-8 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect 
              your information when you use SynqTag.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-ink-soft">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4" />
                <span>Effective Date: January 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4" />
                <span>Last Updated: January 1, 2025</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-12 bg-white border-b border-line">
        <div className="container-width section-padding">
          <div>
            <h2 className="text-2xl font-bold text-ink mb-6">Table of Contents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-3 p-4 rounded-lg border border-line hover:border-primary-300 hover:bg-primary-50 transition-all group"
                  >
                    <Icon className="h-5 w-5 text-ink-soft group-hover:text-primary-600" />
                    <span className="text-ink group-hover:text-primary-700 font-medium">
                      {section.title}
                    </span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 bg-paper-deep">
        <div className="container-width section-padding">
          <div className="max-w-4xl mx-auto space-y-16">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-2xl p-8 shadow-lg"
                >
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-accent-600 rounded-lg flex items-center justify-center">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-ink">
                      {section.title}
                    </h2>
                  </div>
                  
                  <div className="space-y-6">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <h3 className="text-xl font-semibold text-ink mb-3">
                          {item.subtitle}
                        </h3>
                        <p className="text-ink-soft leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container-width section-padding">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <EnvelopeIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-ink mb-6">
              Questions About Your Privacy?
            </h2>
            <p className="text-xl text-ink-soft mb-8">
              If you have any questions about this privacy policy or our data practices, 
              please don't hesitate to contact us.
            </p>
            <div className="bg-paper-deep rounded-2xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-ink mb-2">Email Us</h3>
                  <a href="mailto:team@synqtag.com" className="text-primary-600 hover:text-primary-700">
                    team@synqtag.com
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-ink mb-2">Contact Form</h3>
                  <a href="/contact" className="text-primary-600 hover:text-primary-700">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-800">
                <strong>Data Protection Officer:</strong> For EU residents, you may contact our 
                Data Protection Officer at team@synqtag.com for privacy-related inquiries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Updates Section */}
      <section className="py-12 bg-sidebar text-white">
        <div className="container-width section-padding text-center">
          <div>
            <h3 className="text-xl font-bold mb-4">Policy Updates</h3>
            <p className="text-primary-200 mb-6 max-w-2xl mx-auto">
              We may update this privacy policy from time to time. We will notify you of any 
              material changes by posting the new policy on this page and updating the effective date.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-ink-soft">
              <ClockIcon className="h-4 w-4" />
              <span>We recommend reviewing this policy periodically for any changes.</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};